import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import { useGamification } from '../../contexts/GamificationContext';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import PointsDisplay from '../../components/gamification/PointsDisplay';
import BadgesDisplay from '../../components/gamification/BadgesDisplay';
import ActivityFeed from '../../components/gamification/ActivityFeed';
import * as FiIcons from 'react-icons/fi';

const { FiBriefcase, FiUsers, FiCalendar, FiUser, FiMapPin, FiGlobe, FiSend, FiClock, FiCheck, FiX } = FiIcons;

const CandidateDashboard = () => {
  const { companies, jobs, interviewRequests, interviews, addInterviewRequest } = useData();
  const { user } = useAuth();
  const { addPoints, awardBadge } = useGamification();
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [requestType, setRequestType] = useState('general'); // 'general' or 'specific'
  const [selectedJob, setSelectedJob] = useState('');

  const userRequests = interviewRequests.filter(req => req.candidateId === user.id);
  const userInterviews = interviews.filter(int => int.candidateId === user.id);

  const handleRequestInterview = () => {
    if (!selectedCompany) return;
    
    const request = {
      candidateId: user.id,
      candidateName: user.name,
      companyId: selectedCompany.id,
      companyName: selectedCompany.name,
      type: requestType,
      jobId: requestType === 'specific' ? selectedJob : null,
      jobTitle: requestType === 'specific' ? jobs.find(j => j.id === selectedJob)?.title : null
    };
    
    addInterviewRequest(request);
    
    // Award points for requesting interview
    addPoints('interviewRequested');
    
    // Check for networking badges
    const totalRequests = userRequests.length + 1;
    if (totalRequests >= 5) {
      awardBadge('networker');
    }
    if (totalRequests >= 20) {
      awardBadge('superNetworker');
    }
    
    setSelectedCompany(null);
    setRequestType('general');
    setSelectedJob('');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'text-yellow-600 bg-yellow-100';
      case 'accepted': return 'text-green-600 bg-green-100';
      case 'declined': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return FiClock;
      case 'accepted': return FiCheck;
      case 'declined': return FiX;
      default: return FiClock;
    }
  };

  if (!user.profileComplete) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
          <SafeIcon icon={FiUser} className="text-blue-600 text-4xl mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-blue-900 mb-2">Complete Your Profile</h2>
          <p className="text-blue-700 mb-4">
            Complete your profile to start browsing companies and requesting interviews. 
            <span className="font-semibold"> Earn 50 points!</span>
          </p>
          <Link to="/candidate/profile" className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors inline-flex items-center space-x-2">
            <SafeIcon icon={FiUser} />
            <span>Complete Profile</span>
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Welcome back, {user.name}!</h1>
        <p className="text-gray-600">Discover opportunities and connect with companies at the job fair.</p>
      </motion.div>

      {/* Gamification Section */}
      <div className="grid lg:grid-cols-3 gap-6 mb-8">
        <div className="lg:col-span-2 space-y-6">
          <PointsDisplay />
          <ActivityFeed />
        </div>
        <div className="space-y-6">
          <BadgesDisplay />
        </div>
      </div>

      {/* Stats */}
      <div className="grid md:grid-cols-4 gap-6 mb-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="bg-blue-100 p-3 rounded-lg">
              <SafeIcon icon={FiUsers} className="text-blue-600 text-xl" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Companies</p>
              <p className="text-2xl font-bold text-gray-900">{companies.length}</p>
            </div>
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="bg-green-100 p-3 rounded-lg">
              <SafeIcon icon={FiBriefcase} className="text-green-600 text-xl" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Job Openings</p>
              <p className="text-2xl font-bold text-gray-900">{jobs.length}</p>
            </div>
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="bg-yellow-100 p-3 rounded-lg">
              <SafeIcon icon={FiClock} className="text-yellow-600 text-xl" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Requests</p>
              <p className="text-2xl font-bold text-gray-900">{userRequests.length}</p>
            </div>
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="bg-purple-100 p-3 rounded-lg">
              <SafeIcon icon={FiCalendar} className="text-purple-600 text-xl" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Interviews</p>
              <p className="text-2xl font-bold text-gray-900">{userInterviews.length}</p>
            </div>
          </div>
        </motion.div>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Companies */}
        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.5 }} className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Participating Companies</h2>
          </div>
          <div className="p-6 space-y-4 max-h-96 overflow-y-auto">
            {companies.map((company) => (
              <div key={company.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start space-x-4">
                  <img src={company.logo} alt={company.name} className="w-12 h-12 rounded-lg object-cover" />
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">{company.name}</h3>
                    <p className="text-sm text-gray-600">{company.industry}</p>
                    <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
                      <div className="flex items-center space-x-1">
                        <SafeIcon icon={FiMapPin} />
                        <span>Booth {company.boothNumber}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <SafeIcon icon={FiGlobe} />
                        <span>{jobs.filter(j => j.companyId === company.id).length} jobs</span>
                      </div>
                    </div>
                    <button 
                      onClick={() => setSelectedCompany(company)} 
                      className="mt-3 bg-blue-600 text-white px-4 py-2 rounded-md text-sm hover:bg-blue-700 transition-colors inline-flex items-center space-x-1"
                    >
                      <SafeIcon icon={FiSend} />
                      <span>Request Interview</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Interview Requests & Interviews */}
        <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.6 }} className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">My Requests & Interviews</h2>
          </div>
          <div className="p-6 space-y-4 max-h-96 overflow-y-auto">
            {userRequests.length === 0 ? (
              <p className="text-gray-500 text-center py-8">
                No interview requests yet. Start by requesting interviews with companies!
                <span className="block text-sm mt-2 text-blue-600 font-medium">+10 points per request</span>
              </p>
            ) : (
              userRequests.map((request) => (
                <div key={request.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-semibold text-gray-900">{request.companyName}</h3>
                      <p className="text-sm text-gray-600">
                        {request.type === 'specific' ? request.jobTitle : 'General Interview'}
                      </p>
                      <div className="flex items-center space-x-2 mt-2">
                        <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(request.status)}`}>
                          <SafeIcon icon={getStatusIcon(request.status)} />
                          <span className="capitalize">{request.status}</span>
                        </span>
                        {request.status === 'accepted' && (
                          <span className="text-xs text-green-600 font-medium">+25 points!</span>
                        )}
                      </div>
                      {request.status === 'accepted' && request.interviewTime && (
                        <div className="mt-2 text-sm text-gray-600">
                          <div className="flex items-center space-x-1">
                            <SafeIcon icon={FiCalendar} />
                            <span>{new Date(request.interviewTime).toLocaleString()}</span>
                          </div>
                          <div className="flex items-center space-x-1 mt-1">
                            <SafeIcon icon={FiMapPin} />
                            <span>Booth {request.boothLocation}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </motion.div>
      </div>

      {/* Interview Request Modal */}
      {selectedCompany && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Request Interview with {selectedCompany.name}</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Interview Type</label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input 
                      type="radio" 
                      name="requestType" 
                      value="general" 
                      checked={requestType === 'general'} 
                      onChange={(e) => setRequestType(e.target.value)} 
                      className="mr-2" 
                    />
                    <span>General company interview</span>
                  </label>
                  <label className="flex items-center">
                    <input 
                      type="radio" 
                      name="requestType" 
                      value="specific" 
                      checked={requestType === 'specific'} 
                      onChange={(e) => setRequestType(e.target.value)} 
                      className="mr-2" 
                    />
                    <span>Specific job position</span>
                  </label>
                </div>
              </div>
              {requestType === 'specific' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Select Job Position</label>
                  <select 
                    value={selectedJob} 
                    onChange={(e) => setSelectedJob(e.target.value)} 
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select a job...</option>
                    {jobs
                      .filter(job => job.companyId === selectedCompany.id)
                      .map(job => (
                        <option key={job.id} value={job.id}>
                          {job.title} - {job.level}
                        </option>
                      ))}
                  </select>
                </div>
              )}
            </div>
            <div className="flex space-x-3 mt-6">
              <button 
                onClick={handleRequestInterview} 
                disabled={requestType === 'specific' && !selectedJob}
                className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Send Request
              </button>
              <button 
                onClick={() => setSelectedCompany(null)} 
                className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 transition-colors"
              >
                Cancel
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default CandidateDashboard;